// R5-2 — THE PURE DISPLAY FORMATTERS, LIFTED OUT OF App.tsx VERBATIM.
//
// Every function here is PURE: same input, same output, no React, no DOM, no closure over App state.
// That is the whole entry requirement for this module, and it is what makes these laws gateable —
// `scripts/r5-extraction.mjs` loads this file through esbuild and asserts the behaviour, which was
// impossible while they sat inside an 18,000-line TSX component file.
//
// Bodies are UNCHANGED from App.tsx. A "while I'm in here" edit inside a move is how a move becomes
// a regression; anything that needs fixing gets its own commit after the move lands green.

// A planet as AstrologyAPI returns it (`planets/tropical`). `isRetro` really does come back as both
// a boolean and the string "true" depending on the endpoint, which is why the check below tests both.
export type PlanetPosition = {
  name: string;
  fullDegree: number;
  normDegree: number;
  speed: number;
  isRetro: boolean | string;
  sign: string;
  house: number;
};

export function formatLabel(value: string) {
  return value
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function formatReadableValue(value: unknown): string {
  if (value === null || value === undefined || value === "") {
    return "Not returned";
  }

  if (typeof value === "number") {
    return Number.isInteger(value) ? String(value) : value.toFixed(2);
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (Array.isArray(value)) {
    return value.map(formatReadableValue).join(", ");
  }

  if (typeof value === "object") {
    return Object.entries(value as Record<string, unknown>)
      .slice(0, 8)
      .map(([key, nestedValue]) => `${formatLabel(key)}: ${formatReadableValue(nestedValue)}`)
      .join("; ");
  }

  return String(value);
}

export function formatPlanetPlacements(planets: PlanetPosition[]) {
  return planets.map((planet) => {
    const degree = Number(planet.normDegree);
    const degreeText = Number.isNaN(degree) ? "" : ` at ${degree.toFixed(2)} degrees`;
    const retrograde = planet.isRetro === true || planet.isRetro === "true" ? " retrograde" : "";
    return `- ${planet.name}: ${planet.sign}${degreeText}, House ${planet.house}${retrograde}.`;
  });
}

// X1: a report's subject identity marks WHOSE it is — 'profile' (the member's own card),
// 'person'/'person:<id>' (a circle row), or null (legacy, treated as the member's own). Profile-
// subject surfaces (identity strip, foundational report chips) exclude person subjects so a saved
// person-of-interest chart can never stand in for the member's own.
export function isPersonSubject(subject?: string | null): boolean {
  return typeof subject === "string" && (subject === "person" || subject.startsWith("person:"));
}

export function formatObjectSummary(data: Record<string, unknown>) {
  return Object.entries(data)
    .filter(([, value]) => value !== null && value !== undefined && value !== "")
    .slice(0, 18)
    .map(([key, value]) => `- ${formatLabel(key)}: ${formatReadableValue(value)}`);
}

export function formatResultSummary(result: unknown): string[] {
  if (Array.isArray(result)) {
    if (result.every((item) => item && typeof item === "object" && "name" in item && "sign" in item)) {
      return ["Planet placements:", ...formatPlanetPlacements(result as PlanetPosition[])];
    }

    return result.flatMap((item, index) => [
      `Reading section ${index + 1}:`,
      ...(item && typeof item === "object" ? formatObjectSummary(item as Record<string, unknown>) : [`- ${formatReadableValue(item)}`]),
    ]);
  }

  if (result && typeof result === "object") {
    const objectResult = result as Record<string, unknown>;
    const nestedResult = objectResult.result || objectResult.data || objectResult.response;

    if (nestedResult && nestedResult !== result) {
      return formatResultSummary(nestedResult);
    }

    return formatObjectSummary(objectResult);
  }

  return result ? [formatReadableValue(result)] : [];
}

// Takes the tool STRUCTURALLY (it only ever reads the title) rather than importing App.tsx's
// `AstrologyTool`, so the catalog type stays where the catalog is and this module imports nothing.
export function formatReadingOutput(tool: { title: string }, data: any) {
  const lines = [
    `${tool.title}`,
    "",
    data?.message || "Your reading request has been prepared.",
  ];

  if (data?.result) {
    const readable = formatResultSummary(data.result);
    if (readable.length) {
      lines.push("", ...readable);
    }
  }

  if (data?.vendor) {
    lines.push("", "The private reading service returned a message:");
    lines.push(...formatResultSummary(data.vendor));
  }

  return lines.join("\n");
}

export function titleCase(value: string) {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}

export function ordinalLabel(n: number) {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  return `${n}${["th", "st", "nd", "rd"][n % 10] || "th"}`;
}

export function formatBirthday(iso: string) {
  const [year, month, day] = (iso || "").split("-").map(Number);
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  if (!year || !month || !day) return iso;
  return `${months[month - 1]} ${day}`;
}

export function stripMarkdown(value: string) {
  return value
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/\*(.*?)\*/g, "$1")
    .replace(/__(.*?)__/g, "$1")
    .replace(/`([^`]*)`/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^\s*[-*]\s+/gm, "• ");
}

export function headingSlug(text: string) {
  return String(text).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

// R95 measured that App.tsx held exactly ONE pure top-level function, and ITEM 1 moved it here for
// roadmap 7's own reason: a law inside a TSX file cannot be imported by a `.mjs` gate, so this one
// was held by nothing but a regex over 12,000 lines. The fallback chain is the point — a member with
// no profile name falls to her account name, then to the local part of her address, and only then to
// the house's own word, so no surface ever greets her with an empty string.
export function memberFirstName(
  member: { name?: string | null; email?: string | null } | null,
  profile: { name?: string | null } | null,
) {
  const source = profile?.name || member?.name || member?.email?.split("@")[0] || "Member";
  return source.trim().split(/\s+/)[0] || "Member";
}

/**
 * F1/R8 — **A DATE AS A PERSON READS IT, AND WHAT IT SAYS WHEN THERE IS NO DATE.**
 *
 * Moved out of App.tsx at its declaration when the funnel arm pushed that file over the R8 ceiling
 * (roadmap 7 ratchets it down, never up), and this is where the house's display formatters already
 * live. It was five call sites reading one line inside a twelve-thousand-line file, so no gate could
 * ever run it.
 *
 * **`"Never"` IS THE POINT OF IT (R18).** A null date and a date are two different facts, and this
 * renders on owner surfaces where an empty cell would read as *nothing happened here* exactly as
 * loudly as it reads as *nobody has looked*. The word is what makes the absence legible.
 */
export const shortDate = (v: string | null) =>
  (v ? new Date(v).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "Never");
